import { UITable } from '../../components/table/ui-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { debounce } from '../../global/helpers';
import { browser } from '../../global/index';
import { getBaseGridPoint, updateClassList } from '../../global/ui-helpers';
import { Debounce } from '../../global/utils.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-table editable plugin.
 * @alias UITableEditable
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableEditable implements ComponentPlugin {
    private readonly target: UITable;
    private cellFixator!: Debounce;

    static readonly WidthOffset = getBaseGridPoint() * 3;

    constructor(target: UITable) {
        this.target = target;
    }

    /**
     * Check if table is remove cell for cloneable plugin.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    private isRemoveCell(td: HTMLTableCellElement): boolean {
        return td.classList.contains('-remove');
    }

    /**
     * Check if table data cell contains non elements nodes.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    private hasOnlyTextableNodes(td: HTMLTableCellElement): boolean {
        return (
            Array.from(td.childNodes).filter(
                (child: ChildNode) => child.nodeType === Node.ELEMENT_NODE
            ).length === 0
        );
    }

    /**
     * Fixes the cells widths.
     * @private
     */
    private fixateCells(): void {
        const cells = this.target.querySelectorAll('td');
        for (const cell of cells) {
            if (browser.mobileMediaMatches()) {
                cell.style.width = '';
            } else {
                const rect = cell.getBoundingClientRect();
                const width = rect.width - UITableEditable.WidthOffset;
                cell.style.width = `${width}px`;
            }
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        browser.mobileMedia.addEventListener('change', this.cellFixator);
        browser.tabletMedia.addEventListener('change', this.cellFixator);
        browser.desktopMedia.addEventListener('change', this.cellFixator);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        browser.mobileMedia.removeEventListener('change', this.cellFixator);
        browser.tabletMedia.removeEventListener('change', this.cellFixator);
        browser.desktopMedia.removeEventListener('change', this.cellFixator);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-editable');
        const rows = this.target.querySelectorAll('tbody tr');
        if (!rows.length) {
            return;
        }

        this.target
            .querySelectorAll<HTMLTableCellElement>('tbody td')
            .forEach((cell: HTMLTableCellElement) => {
                const isTopAligned: boolean =
                    !this.isRemoveCell(cell) &&
                    !this.hasOnlyTextableNodes(cell);
                updateClassList(cell, {
                    'col': true,
                    '-valign-top': isTopAligned,
                });
            });
        this.fixateCells();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.cellFixator = debounce(this.fixateCells.bind(this), 100);
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }
}

UITable.registerPlugin('editable', UITableEditable);
